/**
 * FlowPasswordManager — standing self-serve panel for flow credential
 * management (story 24111, Phase 4).
 *
 * Regular users can:
 *   1. View the status of each flow credential (set / needs_update / not_set).
 *      No password value is ever displayed.
 *   2. Update any credential via a masked input form (write-only).
 *   3. After a successful update the status list auto-refreshes.
 *
 * PRD AC-1 (view status), AC-2 (no secret display), AC-6 (update form),
 * AC-7 (status refresh after update).
 *
 * Design patterns:
 *   - Async load follows the same useState/useEffect pattern as LaunchTab.
 *   - Submit error handling follows the CredentialEntryCardWrapper pattern
 *     (useState<Error|null>, ErrorPanel) that the retired
 *     HitlEventDispatcher.tsx used before the pane moved to DecisionCard.
 *   - CredentialEntryCard reuse for the masked input (shared card, shared
 *     sensitivity.ts masking — `format:"password"` enforced by that card).
 *   - Never calls `decrypt_from_kv` or displays a credential value.
 */
import { useCallback, useEffect, useState } from "react";
import CredentialEntryCard from "@product-app/common/workflows/components/CredentialEntryCard";
import ErrorPanel from "./ErrorPanel";
import { orchestratorApi } from "../services/orchestratorApi";
import type { UserCredentialStatus } from "../services/orchestratorApi";

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<UserCredentialStatus["status"], string> = {
  set: "Active",
  needs_update: "Needs update",
  not_set: "Not set",
};

const STATUS_CLASS: Record<UserCredentialStatus["status"], string> = {
  set: "bg-primary-success-50 border-primary-success-200 text-primary-success-700",
  needs_update: "bg-primary-warning-50 border-primary-warning-300 text-primary-warning-700",
  not_set: "bg-primary-gray-light-100 border-primary-gray-light-300 text-primary-gray-light-600",
};

interface StatusChipProps {
  status: UserCredentialStatus["status"];
}

function StatusChip({ status }: StatusChipProps) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-[2px] text-2xs font-semibold ${STATUS_CLASS[status]}`}
      data-testid={`credential-status-chip-${status}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Credential row — shows status and launches the update form inline.
// ---------------------------------------------------------------------------

interface CredentialRowProps {
  credential: UserCredentialStatus;
  onUpdateSuccess: () => void;
}

function CredentialRow({ credential, onUpdateSuccess }: CredentialRowProps) {
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const displayName = credential.display_name ?? credential.credential_name;

  /**
   * Build a minimal credential_save HITL question shape so the shared
   * CredentialEntryCard renders the correct display name and masked input.
   * The card reads `hitlQuestion.data.display_name` for the form title.
   *
   * IMPORTANT: No value field is ever included here or anywhere in this
   * component's state — the card is write-only.
   */
  const hitlQuestion = {
    type: "credential_save",
    data: {
      display_name: displayName,
      description: `Enter the password for "${displayName}". It will be stored securely and used by your flows on your behalf.`,
    },
  };

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setSubmitting(true);
      setError(null);
      try {
        const value = values.value as string | undefined;
        if (!value) {
          throw new Error("Password is required.");
        }
        await orchestratorApi.updateMyFlowPassword(credential.credential_name, value);
        setEditing(false);
        onUpdateSuccess();
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setSubmitting(false);
      }
    },
    [credential.credential_name, onUpdateSuccess],
  );

  const handleCancel = useCallback(() => {
    setEditing(false);
    setError(null);
  }, []);

  return (
    <div
      className="rounded-lg border border-primary-gray-light-200 bg-base-white p-3 flex flex-col gap-2"
      data-testid={`credential-row-${credential.credential_name}`}
    >
      {/* Header row: name + status chip */}
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span
            className="text-sm font-semibold text-primary-gray-light-900 truncate"
            data-testid="credential-display-name"
          >
            {displayName}
          </span>
          {credential.last_updated && (
            <span className="text-2xs text-primary-gray-light-500">
              Last updated:{" "}
              {new Date(credential.last_updated).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          )}
        </div>
        <StatusChip status={credential.status} />
      </div>

      {/* Expiry / stale prompt — shown when the credential needs attention */}
      {credential.status === "needs_update" && !editing && (
        <p
          className="text-xs text-primary-warning-700 bg-primary-warning-50 rounded px-2 py-1 border border-primary-warning-200"
          data-testid="credential-stale-prompt"
        >
          This credential needs to be updated — your flows may be failing. Please enter a new
          password below.
        </p>
      )}

      {/* Not-set prompt */}
      {credential.status === "not_set" && !editing && (
        <p
          className="text-xs text-primary-gray-light-600"
          data-testid="credential-not-set-prompt"
        >
          No password saved yet. Add one so your flows can run on your behalf.
        </p>
      )}

      {/* Update form (inline) */}
      {editing ? (
        <div className="flex flex-col gap-2">
          {error && (
            <ErrorPanel
              error={error}
              title="Couldn't save credentials"
              description="Your credentials weren't saved. You can try again."
              compact
            />
          )}
          <CredentialEntryCard
            hitlQuestion={hitlQuestion}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Save password"
            submittingOverride={submitting}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setError(null);
            setEditing(true);
          }}
          className="btn-secondary-gray self-start text-xs px-3 py-1"
          data-testid="credential-update-button"
        >
          Update password
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FlowPasswordManager — top-level panel
// ---------------------------------------------------------------------------

export interface FlowPasswordManagerProps {
  /** Optional close handler — used when the panel is mounted in a drawer. */
  onClose?: () => void;
}

export default function FlowPasswordManager({ onClose }: FlowPasswordManagerProps) {
  const [credentials, setCredentials] = useState<UserCredentialStatus[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const items = await orchestratorApi.getMyCredentialStatus();
      setCredentials(items);
    } catch (err) {
      setLoadError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  return (
    <div
      className="flex flex-col gap-4 p-4"
      data-testid="flow-password-manager"
      aria-label="Flow password manager"
    >
      {/* Panel header */}
      <div className="flex flex-row items-center justify-between">
        <h2 className="text-base font-bold text-primary-gray-light-900">Flow passwords</h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded border-none bg-transparent px-2 py-1 text-xs text-primary-gray-light-400 hover:text-primary-gray-light-600"
            aria-label="Close flow passwords panel"
            data-testid="flow-password-manager-close"
          >
            Close
          </button>
        )}
      </div>

      <p className="text-sm text-primary-gray-light-600">
        Manage the passwords your flows use on your behalf. Passwords are stored securely and
        never displayed.
      </p>

      {/* Load states */}
      {loading && (
        <div
          className="flex items-center justify-center py-8 text-sm text-primary-gray-light-500"
          data-testid="flow-password-manager-loading"
          role="status"
          aria-live="polite"
        >
          Loading…
        </div>
      )}

      {!loading && loadError && (
        <ErrorPanel
          error={loadError}
          title="Couldn't load credentials"
          description="We couldn't fetch your flow credentials. Please try again."
          onRetry={() => void loadStatus()}
        />
      )}

      {!loading && !loadError && credentials !== null && credentials.length === 0 && (
        <p
          className="text-sm text-primary-gray-light-500 py-4 text-center"
          data-testid="flow-password-manager-empty"
        >
          No flow credentials configured for your account.
        </p>
      )}

      {/* Credential list */}
      {!loading && !loadError && credentials !== null && credentials.length > 0 && (
        <div
          className="flex flex-col gap-3"
          data-testid="flow-password-manager-list"
        >
          {credentials.map((cred) => (
            <CredentialRow
              key={cred.credential_name}
              credential={cred}
              onUpdateSuccess={() => void loadStatus()}
            />
          ))}
        </div>
      )}
    </div>
  );
}
